import React, {useCallback, useMemo} from 'react';
import {
    Button,
    ButtonGroup,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
    ChecklistItemCategory,
    NewJoinerChecklistItem,
    NewJoinerChecklistItemFilter,
    NewJoinerChecklistItemForm
} from '../../api/Model';
import {inputChangeEvent, SetState} from '../../util/FormHandlers';
import CloseIcon from "@mui/icons-material/Close";
import {appendElement, deleteElementByIndex, replaceElementByIndex} from "../../util/ArrayUtil";
import {grey} from "@mui/material/colors";
import {newJoinerLinkPlaceholders} from "./NewJoinerApi";
import {ChecklistItemFilterSelect} from "./ChecklistItemFilterSelect";
import {NewJoinerRemoteTag} from "./NewJoinerTypeTag";
import {toNewJoinerChecklistItemForm} from "../../api/Projections";

const CATEGORY_LABELS = [
    {category: ChecklistItemCategory.Neighbourhood, label: 'Neighbourhood', officeOnly: true},
    {category: ChecklistItemCategory.Key, label: 'Key', officeOnly: true},
    {category: ChecklistItemCategory.WelcomePack, label: 'Welcome Pack', officeOnly: false},
    {category: ChecklistItemCategory.Standalone, label: 'Other Checklist Items', officeOnly: false},
];

type DraftItem = {
    key: string,
    original?: NewJoinerChecklistItem,
    modified: boolean,
} & NewJoinerChecklistItemForm;


type CategoryDraft = {
    category: ChecklistItemCategory,
    label: string,
    officeOnly: boolean,
    items: DraftItem[],
}

export type NewJoinerChecklistForm = {
    initialItems: NewJoinerChecklistItem[],
    categories: CategoryDraft[],
    nextNewKey: number,
    deleted: NewJoinerChecklistItem[],
};


export function createNewJoinerChecklistForm(items: NewJoinerChecklistItem[]): NewJoinerChecklistForm {
    return {
        initialItems: items,
        categories: CATEGORY_LABELS.map(({category, label, officeOnly}) => ({
            category,
            label,
            officeOnly,
            items: items
                .filter(item => item.category === category)
                .map(item => ({
                    ...toNewJoinerChecklistItemForm(item),
                    key: `original-${item.id}`,
                    original: item,
                    modified: false,
                })),
        })),
        nextNewKey: 0,
        deleted: [],
    };
}

function patchCategory(
    setForm: SetState<NewJoinerChecklistForm>,
    category: ChecklistItemCategory,
    patch: (prevForm: NewJoinerChecklistForm, categoryIndex: number, categoryDraft: CategoryDraft) => NewJoinerChecklistForm,
) {
    setForm(prevForm => {
        const categoryIndex = prevForm.categories
            .findIndex(it => it.category === category);
        return categoryIndex >= 0
            ? patch(prevForm, categoryIndex, prevForm.categories[categoryIndex])
            : prevForm
            ;
    });
}

function patchItem(
    setForm: SetState<NewJoinerChecklistForm>,
    item: DraftItem,
    patch: (prevForm: NewJoinerChecklistForm, categoryIndex: number, categoryDraft: CategoryDraft, itemIndex: number) => NewJoinerChecklistForm,
) {
    return patchCategory(setForm, item.category, (prevForm, categoryIndex, categoryDraft) => {
        const itemIndex: number = categoryDraft.items.indexOf(item);
        return itemIndex >= 0
            ? patch(prevForm, categoryIndex, categoryDraft, itemIndex)
            : prevForm
            ;
    });
}

function createItem(
    setForm: SetState<NewJoinerChecklistForm>,
    category: ChecklistItemCategory,
) {
    patchCategory(setForm, category, (prevForm, categoryIndex, categoryDraft) => ({
        ...prevForm,
        categories: replaceElementByIndex(prevForm.categories, categoryIndex, {
            ...categoryDraft,
            items: appendElement(categoryDraft.items, {
                key: `new-${prevForm.nextNewKey}`,
                modified: true,
                category: categoryDraft.category,
                label: '',
                ordinal: categoryDraft.items.length > 0 ? categoryDraft.items[categoryDraft.items.length - 1].ordinal + 1 : 0,
                filters: [],
            }),
        }),
        nextNewKey: prevForm.nextNewKey + 1,
    }));
}

function updateItem(
    setForm: SetState<NewJoinerChecklistForm>,
    item: DraftItem,
    patch: Partial<NewJoinerChecklistItemForm>,
) {
    patchItem(setForm, item, (prevForm, categoryIndex, categoryDraft, itemIndex) => ({
        ...prevForm,
        categories: replaceElementByIndex(prevForm.categories, categoryIndex, {
            ...categoryDraft,
            items: replaceElementByIndex(categoryDraft.items, itemIndex, {
                ...item,
                ...patch,
                modified: true,
            }),
        }),
    }));
}

function deleteItem(setForm: SetState<NewJoinerChecklistForm>, item: DraftItem) {
    patchItem(setForm, item, (prevForm, categoryIndex, categoryDraft, itemIndex) => ({
        ...prevForm,
        categories: replaceElementByIndex(prevForm.categories, categoryIndex, {
            ...categoryDraft,
            items: deleteElementByIndex(categoryDraft.items, itemIndex),
        }),
        deleted: item.original ? [...prevForm.deleted, item.original] : prevForm.deleted,
    }));
}

function moveUp(setForm: SetState<NewJoinerChecklistForm>, item: DraftItem) {
    patchItem(setForm, item, (prevForm, categoryIndex, categoryDraft, itemIndex) => {
        if (categoryIndex === 0 && itemIndex === 0) {
            // Already at the top
            return prevForm;
        } else if (itemIndex > 0) {
            // Re-order in the same category
            const prevItem = categoryDraft.items[itemIndex - 1];
            return {
                ...prevForm,
                categories: replaceElementByIndex(prevForm.categories, categoryIndex, {
                    ...categoryDraft,
                    items: [
                        ...categoryDraft.items.slice(0, itemIndex - 1),
                        {
                            ...item,
                            ordinal: prevItem.ordinal,
                            modified: true,
                        },
                        {
                            ...prevItem,
                            ordinal: item.ordinal,
                            modified: true,
                        },
                        ...categoryDraft.items.slice(itemIndex + 1),
                    ],
                }),
            };
        } else {
            // Move to previous category
            const prevCategory = prevForm.categories[categoryIndex - 1];
            const lastOrdinal = prevCategory.items.length > 0 ? prevCategory.items[prevCategory.items.length - 1].ordinal + 1 : 0;
            return {
                ...prevForm,
                categories: [
                    ...prevForm.categories.slice(0, categoryIndex - 1),
                    {
                        ...prevCategory,
                        items: appendElement(prevCategory.items, {
                                ...item,
                                category: prevCategory.category,
                                ordinal: lastOrdinal,
                                modified: true,
                            }
                        ),
                    },
                    {
                        ...categoryDraft,
                        items: deleteElementByIndex(categoryDraft.items, itemIndex),
                    },
                    ...prevForm.categories.slice(categoryIndex + 1),
                ],
            };
        }

    });
}

function moveDown(setForm: SetState<NewJoinerChecklistForm>, item: DraftItem) {
    patchItem(setForm, item, (prevForm, categoryIndex, categoryDraft, itemIndex) => {
        if (categoryIndex === prevForm.categories.length - 1 && itemIndex === categoryDraft.items.length - 1) {
            // Already at the bottom
            return prevForm;
        } else if (itemIndex < categoryDraft.items.length - 1) {
            // Re-order in the same category
            const nextItem = categoryDraft.items[itemIndex + 1];
            return {
                ...prevForm,
                categories: replaceElementByIndex(prevForm.categories, categoryIndex, {
                    ...categoryDraft,
                    items: [
                        ...categoryDraft.items.slice(0, itemIndex),
                        {
                            ...nextItem,
                            ordinal: item.ordinal,
                            modified: true,
                        },
                        {
                            ...item,
                            ordinal: nextItem.ordinal,
                            modified: true,
                        },
                        ...categoryDraft.items.slice(itemIndex + 2),
                    ],
                }),
            };
        } else {
            // Move to next category
            const nextCategory = prevForm.categories[categoryIndex + 1];
            const firstOrdinal = nextCategory.items.length > 0 ? nextCategory.items[0].ordinal - 1 : 0;
            return {
                ...prevForm,
                categories: [
                    ...prevForm.categories.slice(0, categoryIndex),
                    {
                        ...categoryDraft,
                        items: deleteElementByIndex(categoryDraft.items, itemIndex),
                    },
                    {
                        ...nextCategory,
                        items: [
                            {
                                ...item,
                                category: nextCategory.category,
                                ordinal: firstOrdinal,
                                modified: true,
                            },
                            ...nextCategory.items,
                        ],
                    },
                    ...prevForm.categories.slice(categoryIndex + 2),
                ],
            };
        }
    });
}

export async function saveNewJoinerChecklistForm(
    form: NewJoinerChecklistForm,
    createNewJoinerChecklistItem: (form: NewJoinerChecklistItemForm) => Promise<NewJoinerChecklistItem>,
    updateNewJoinerChecklistItem: (item: NewJoinerChecklistItem, form: NewJoinerChecklistItemForm) => Promise<NewJoinerChecklistItem>,
    deleteNewJoinerChecklistItem: (item: NewJoinerChecklistItem) => Promise<void>,
) {
    for (const item of form.categories.flatMap(category => category.items)) {
        if (item.modified) {
            const itemForm = toNewJoinerChecklistItemForm(item);
            if (item.original) {
                await updateNewJoinerChecklistItem(item.original, itemForm)
            } else {
                await createNewJoinerChecklistItem(itemForm);
            }
        }
    }
    for (const item of form.deleted) {
        await deleteNewJoinerChecklistItem(item);
    }
}

function ItemEditor({item, setForm, isFirst, isLast}: {
    item: DraftItem,
    setForm: SetState<NewJoinerChecklistForm>,
    isFirst: boolean,
    isLast: boolean,
}) {
    const handlers = useMemo(() => ({
        handleEditLabel: inputChangeEvent(newValue => updateItem(setForm, item, {label: newValue})),
        handleEditLink: inputChangeEvent(newValue => updateItem(setForm, item, {link: newValue})),
        handleEditFilters: (filters: NewJoinerChecklistItemFilter[]) => updateItem(setForm, item, {filters}),
        handleDelete: () => deleteItem(setForm, item),
        handleMoveUp: () => moveUp(setForm, item),
        handleMoveDown: () => moveDown(setForm, item),
    }), [setForm, item]);

    return (
        <Card variant="outlined">
            <CardHeader
                title={
                    <TextField
                        label="Label"
                        size="small"
                        value={item.label}
                        onChange={handlers.handleEditLabel}
                        sx={{minWidth: '260px'}}
                    />
                }
                action={
                    <ButtonGroup>
                        <Tooltip title="Move Down">
                            <IconButton onClick={handlers.handleMoveDown} disabled={isLast}>
                                <ArrowDownwardIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Move Up">
                            <IconButton onClick={handlers.handleMoveUp} disabled={isFirst}>
                                <ArrowUpwardIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                            <IconButton onClick={handlers.handleDelete}>
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    </ButtonGroup>
                }
            />
            <CardContent>
                <Stack direction="column" flexGrow={1} gap={2}>
                    <TextField
                        label="Link"
                        size="small"
                        value={item.link ?? ''}
                        onChange={handlers.handleEditLink}
                        placeholder="https://..."
                        helperText={
                            <Typography variant="inherit" component="span">
                                Supported placeholders:
                                {newJoinerLinkPlaceholders.map((placeholder, index) => (
                                    <span key={placeholder.pattern}>
                                        {index > 0 && ', '}
                                        <Tooltip title={placeholder.description}>
                                            <Typography component="span" variant="inherit" sx={{
                                                backgroundColor: grey[200],
                                                px: 0.5,
                                                py: '2px',
                                                ml: 0.5,
                                                borderRadius: '4px',
                                            }}>{placeholder.pattern}</Typography>
                                        </Tooltip>
                                    </span>
                                ))}
                            </Typography>
                        }
                        sx={{flex: 3, minWidth: 260}}
                    />
                    <ChecklistItemFilterSelect
                        filters={item.filters ?? []}
                        onChange={handlers.handleEditFilters}
                    />
                </Stack>
            </CardContent>
        </Card>
    );
}

function CategoryEditor({category, setForm, isFirst, isLast}: {
    category: CategoryDraft,
    setForm: SetState<NewJoinerChecklistForm>,
    isFirst: boolean,
    isLast: boolean,
}) {
    const handleAddItem = useCallback(
        () => createItem(setForm, category.category),
        [setForm, category.category],
    );

    return (
        <Card sx={{backgroundColor: grey["50"]}}>
            <CardHeader
                title={
                    <Stack direction="row" gap={1} alignItems="baseline">
                        <Typography variant="inherit">{category.label}</Typography>
                        {category.officeOnly && (
                            <NewJoinerRemoteTag remote={false} tooltip="Shown only for the employees assigned to an office"/>
                        )}
                    </Stack>
                }
            />
            <CardContent>
                <Stack direction="column" gap={1}>
                    {category.items.map((item, itemIndex) => (
                        <ItemEditor
                            key={item.key}
                            item={item}
                            setForm={setForm}
                            isFirst={isFirst && (itemIndex === 0)}
                            isLast={isLast && (itemIndex === category.items.length - 1)}
                        />
                    ))}
                </Stack>
            </CardContent>
            <CardActions>
                <Button
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={handleAddItem}
                >
                    Add item
                </Button>
            </CardActions>
        </Card>
    );
}

export function ChecklistManagementResetButton({setForm}: {
    setForm: SetState<NewJoinerChecklistForm>,
}) {
    const handleReset = useCallback(() => {
        setForm(prevForm => createNewJoinerChecklistForm(prevForm.initialItems));
    }, [setForm]);

    return (
        <Button variant="outlined" color="secondary" onClick={handleReset}>Reset</Button>
    );
}

export function ChecklistManagementForm({form, setForm}: {
    form: NewJoinerChecklistForm,
    setForm: SetState<NewJoinerChecklistForm>,
}) {
    return (
        <Stack gap={3}>
            {form.categories.map((category, categoryIndex) => (
                <CategoryEditor
                    key={category.category}
                    category={category}
                    setForm={setForm}
                    isFirst={categoryIndex === 0}
                    isLast={categoryIndex === form.categories.length - 1}
                />
            ))}
        </Stack>
    );
}
